import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axios';
import { saveActiveAccountId } from '../utils/persist';

export const fetchPerson = createAsyncThunk(
  'globalData/fetchPerson',
  async (_, { getState, rejectWithValue }) => {
    const email = getState().auth?.user?.email;
    if (!email) return rejectWithValue('No email in auth state');
    const res = await api.get(`/mvapi/people/email/${encodeURIComponent(email)}`);
    return res.data; 
  }
);

export const fetchSenders = createAsyncThunk('globalData/fetchSenders', async (accountId) => {
  const res = await api.get(`/mvapi/utils/senders/account/${accountId}`);
  return res.data;
});
export const fetchConnectors = createAsyncThunk('globalData/fetchConnectors', async (accountId) => {
  const res = await api.get(`/mvapi/connectors/account/${accountId}`);
  return res.data;
});
export const fetchPolicies = createAsyncThunk('globalData/fetchPolicies', async (accountId) => {
  const res = await api.get(`/mvapi/utils/optout/account/${accountId}`);
  return res.data;
});
export const fetchTemplates = createAsyncThunk('globalData/fetchTemplates', async (accountId) => {
  const res = await api.get(`/mvapi/accounts/${accountId}/templates`);
  return res.data;
});
export const fetchAccount = createAsyncThunk('globalData/fetchAccount', async (accountId) => {
  const res = await api.get(`/mvapi/organization/${accountId}`);
  return res.data;
});
export const fetchUsers = createAsyncThunk('globalData/fetchUsers', async (accountId) => {
  const res = await api.get(`/mvapi/people/account/${accountId}`);
  return res.data;
});

const pickDefaultAccountId = (accounts = []) => {
  const def = accounts.find(a => a.default === true || a.isDefault === true);
  return def?.accountId || accounts[0]?.accountId || null;
};

export const fetchOrgScopedFor = createAsyncThunk(
  'globalData/fetchOrgScopedFor',
  async (accountId, { dispatch }) => {
    if (!accountId) throw new Error('fetchOrgScopedFor requires accountId');
    await Promise.all([
      dispatch(fetchSenders(accountId)),
      dispatch(fetchConnectors(accountId)),
      dispatch(fetchPolicies(accountId)),
      dispatch(fetchTemplates(accountId)),
      dispatch(fetchAccount(accountId)),
      dispatch(fetchUsers(accountId)),
    ]);
    return accountId; 
  }
);

export const initializeGlobalData = createAsyncThunk(
  'globalData/init',
  async (_arg, { dispatch, getState }) => {
    const person = await dispatch(fetchPerson()).unwrap();

    const state = getState();
    const current = state.globalData.activeAccountId;
    const chosen = current || pickDefaultAccountId(person?.accounts);

    if (chosen) {
      dispatch(setActiveAccountId(chosen));        // sets initialized=true (see step 3)
      await dispatch(fetchOrgScopedFor(chosen));  
    }

    return { accountId: chosen };
  }
);

const globalDataSlice = createSlice({
  name: 'globalData',
  initialState: {
    person: null,
    senders: null,
    connectors: null,
    policies: null,
    templates: null,
    account: null,
    activeAccountId: null,
    initialized: false,
    dataLoading: false,
    users: null,
    error: null,
  },
  reducers: {
    setActiveAccountId(state, action) {
      state.activeAccountId = action.payload;
      state.initialized = true;    
      saveActiveAccountId(action.payload);  
    },
  },
  extraReducers: (builder) => {
    const addCases = (thunk, key) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.dataLoading = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.dataLoading = false;
          state[key] = action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.dataLoading = false;
          state.error = action.error?.message || 'Request failed';
        });
    };

    addCases(fetchSenders, 'senders');
    addCases(fetchConnectors, 'connectors');
    addCases(fetchPolicies, 'policies');
    addCases(fetchTemplates, 'templates');
    addCases(fetchAccount, 'account');
    addCases(fetchUsers, 'users');
    // fetchPerson handled explicitly to set default activeAccountId if missing
    builder
      .addCase(fetchPerson.pending, (state) => {
        state.dataLoading = true; state.error = null;
      })
      .addCase(fetchPerson.fulfilled, (state, action) => {
        state.dataLoading = false;
        state.person = action.payload;
        if (!state.initialized && !state.activeAccountId) {
        
        let saved = null;
         try { saved = localStorage.getItem('activeAccountId'); } catch {}
         if (saved) {
           state.activeAccountId = saved;
           state.initialized = true;
         } else {
            state.activeAccountId = pickDefaultAccountId(action.payload?.accounts);
         }  
}
        
      })
      .addCase(fetchPerson.rejected, (state, action) => {
        state.dataLoading = false;
        state.error = action.payload || action.error?.message || 'Request failed';
      });

    builder
      .addCase(fetchOrgScopedFor.pending, (state) => {
        state.dataLoading = true;
      })
      .addCase(fetchOrgScopedFor.fulfilled, (state) => {
        state.dataLoading = false;
      })
      .addCase(fetchOrgScopedFor.rejected, (state, action) => {
        state.dataLoading = false;
        state.error = action.error?.message || 'Request failed';
      })
      .addCase(initializeGlobalData.fulfilled, (state, action) => {
        if (action.payload?.accountId) {
          state.activeAccountId = action.payload.accountId;
          state.initialized = true;
          try { localStorage.setItem('activeAccountId', action.payload.accountId); } catch {}
        }
      });
  },
});

export const { setActiveAccountId } = globalDataSlice.actions;

export const selectCurrentAccount = (state) =>
  state.globalData.person?.accounts?.find(
    (acc) => acc.accountId === state.globalData.activeAccountId
  ) ?? null;

export const selectCurrentRole = (state) =>
  selectCurrentAccount(state)?.role ?? null;


export default globalDataSlice.reducer;
